import { useCallback, useMemo, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { NurseData } from "../../../utils/models";
import type { RemoveStaffRow } from "./removeStaffRows";
import { REMOVE_STAFF_ALL_FILTER, REMOVE_STAFF_ANALYTICS_SOURCE, type RemoveStaffWindowHint, resolveWindowFilter } from "./removeStaffUtils";
import { REMOVE_STAFF_OTHER_WINDOW_KEY, type RemoveStaffShiftPlacement, type RemoveStaffWindow } from "./removeStaffWindows";

type Args = {
	rows: readonly RemoveStaffRow[];
	windows: readonly RemoveStaffWindow[];
	otherCount: number;
	placementByShiftId: ReadonlyMap<string, RemoveStaffShiftPlacement>;
	/** Where the caller wants the list to open; `null` = "All shifts". */
	hint: RemoveStaffWindowHint | null;
};

/** The window a row is listed under. */
export const windowKeyOfRow = (row: Pick<NurseData, "shiftId">, placementByShiftId: ReadonlyMap<string, RemoveStaffShiftPlacement>): string => {
	const placement = row.shiftId ? placementByShiftId.get(row.shiftId) : undefined;
	// DEFAULT: a listed shift with no hours to place can't sit in any window, so it is listed under Other.
	return placement ? placement.windowKey : REMOVE_STAFF_OTHER_WINDOW_KEY;
};

/**
 * The picks the active pill hides, counted per window — "All shifts" hides none. What lets a pill say "2 picked"
 * so a pick out of view is never out of mind.
 */
export const countHiddenPicks = (
	picked: readonly RemoveStaffRow[],
	activeKey: string,
	placementByShiftId: ReadonlyMap<string, RemoveStaffShiftPlacement>,
): ReadonlyMap<string, number> => {
	const counts = new Map<string, number>();
	if (activeKey === REMOVE_STAFF_ALL_FILTER) return counts;
	for (const row of picked) {
		const key = windowKeyOfRow(row, placementByShiftId);
		// DEFAULT: a window's first hidden pick starts its count at zero.
		if (key !== activeKey) counts.set(key, (counts.get(key) ?? 0) + 1);
	}
	return counts;
};

/**
 * The window pills' state: the active pill (the user's pick, else the caller's hint, else "All shifts" — and
 * back to "All shifts" whenever the active window has nobody on this date), and the rows it shows.
 */
export const useRemoveStaffFilter = ({ rows, windows, otherCount, placementByShiftId, hint }: Args) => {
	const { track } = useAnalytics();
	const [explicit, setExplicit] = useState<string | null>(null);
	const activeKey = resolveWindowFilter({ explicit, hint, windows, otherCount });

	const visibleRows = useMemo(
		() => (activeKey === REMOVE_STAFF_ALL_FILTER ? [...rows] : rows.filter((row) => windowKeyOfRow(row, placementByShiftId) === activeKey)),
		[rows, activeKey, placementByShiftId],
	);

	const changeFilter = useCallback(
		(key: string) => {
			setExplicit(key);
			track("remove_staff_filter_window", { source: REMOVE_STAFF_ANALYTICS_SOURCE, window_key: key });
		},
		[track],
	);

	return { activeKey, visibleRows, changeFilter };
};
